import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Card, Dropdown, ListGroup } from 'react-bootstrap';
import { isIterableArray } from 'helpers/utils';
import {
  useMarkInboxReadMutation,
  useMarkAllInboxReadMutation
} from 'rtk/notificationInbox.slice';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Notification from 'components/notification/Notification';
import DesktopNotificationControl from 'components/notification/DesktopNotificationControl';
import { enableDesktopNotifications } from 'util/desktopNotifications';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { actionMarkNotificationRead } from 'stores/notification';

const navbarMutedIconClassName = 'text-secondary dark__text-700';

const NotificationDropdown = () => {
  // State
  const [newNotifications, setNewNotifications] = useState([]);
  const [earlierNotifications, setEarlierNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [readError, setReadError] = useState('');

  const dispatch = useDispatch();

  const {
    notifications,
    inboxUnreadCount = 0,
    inboxError,
    inboxOwnerGuid
  } = useSelector(state => state.notification);
  const [markInboxRead] = useMarkInboxReadMutation();
  const [markAllInboxRead, markAllState] = useMarkAllInboxReadMutation();

  // Handler
  const handleToggle = next => {
    setIsOpen(typeof next === 'boolean' ? next : false);
  };

  useEffect(() => {
    // Ask once per session on login; the browser still owns the final choice.
    enableDesktopNotifications().catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth < 1200) setIsOpen(false);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sorted = [...notifications].sort(
      (a, b) => new Date(b.time) - new Date(a.time)
    );
    setNewNotifications(sorted.filter(item => item.unread));
    setEarlierNotifications(sorted.filter(item => !item.unread));
    setUnreadNotificationCount(
      inboxUnreadCount +
        notifications.filter(item => !item.persistent && item.unread).length
    );
  }, [notifications, inboxUnreadCount]);

  const markAsRead = async e => {
    e.preventDefault();
    if (markAllState.isLoading) return;
    try {
      setReadError('');
      if (inboxOwnerGuid) await markAllInboxRead().unwrap();
      dispatch(actionMarkNotificationRead(null));
    } catch {
      setReadError('Unable to mark notifications as read. Please try again.');
    }
  };
  const readItem = async notification => {
    setIsOpen(false);
    if (!notification.unread) return;
    try {
      setReadError('');
      if (notification.persistent)
        await markInboxRead(notification.id).unwrap();
      dispatch(actionMarkNotificationRead(notification.id));
    } catch {
      setReadError(
        'Unable to mark the notification as read. Please try again.'
      );
    }
  };

  return (
    <Dropdown navbar={true} as="li" show={isOpen} onToggle={handleToggle}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as="button"
        type="button"
        aria-label="Notifications"
        style={{ border: 0, background: 'transparent' }}
        className={classNames('px-0 nav-link', {
          'notification-indicator notification-indicator-primary':
            unreadNotificationCount > 0
        })}
      >
        <FontAwesomeIcon
          icon="bell"
          transform="shrink-6"
          className={`fs-4 ${navbarMutedIconClassName}`}
        />
        {unreadNotificationCount > 0 && (
          <span className="notification-indicator-number">
            {unreadNotificationCount}
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg">
        <Card
          className="dropdown-menu-notification dropdown-menu-end shadow-none"
          style={{ maxWidth: '20rem' }}
        >
          <FalconCardHeader
            className="card-header"
            title="Notifications"
            titleTag="h6"
            light={false}
            endEl={
              <button
                type="button"
                className="card-link fw-normal"
                onClick={markAsRead}
                style={{ border: 0, background: 'transparent' }}
                aria-disabled={markAllState.isLoading}
              >
                Mark all as read
              </button>
            }
          />
          <ListGroup
            variant="flush"
            className="fw-normal fs--1 scrollbar"
            style={{ maxHeight: '19rem' }}
          >
            {(inboxError || readError) && (
              <ListGroup.Item role="alert">
                {readError || inboxError}
              </ListGroup.Item>
            )}
            <div className="list-group-title">NEW</div>{' '}
            {isIterableArray(newNotifications) &&
              newNotifications.map(notification => (
                <ListGroup.Item
                  key={notification.id}
                  onClick={() => readItem(notification)}
                >
                  <Notification {...notification} flush />
                </ListGroup.Item>
              ))}
            {!isIterableArray(newNotifications) && (
              <ListGroup.Item key="no-new-notification">
                <p className="text-center text-muted">No new notifications</p>
              </ListGroup.Item>
            )}
            <div className="list-group-title">EARLIER</div>
            {isIterableArray(earlierNotifications) &&
              earlierNotifications.map(notification => (
                <ListGroup.Item
                  key={notification.id}
                  onClick={() => readItem(notification)}
                >
                  <Notification {...notification} flush />
                </ListGroup.Item>
              ))}
            {!isIterableArray(earlierNotifications) && (
              <ListGroup.Item key="no-earlier-notification">
                <p className="text-center text-muted">
                  No earlier notifications
                </p>
              </ListGroup.Item>
            )}
          </ListGroup>
          <div
            className="card-footer text-center border-top"
            onClick={handleToggle}
          >
            <Link className="card-link d-block" to="/account/notifications">
              View all
            </Link>
          </div>
          <div className="card-footer border-top">
            <DesktopNotificationControl />
          </div>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
